import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { TeamOwnerNote } from '../../../team/TeamOwnerNote';
import { useWorkspaceOwner } from '../../../team/useWorkspaceOwner';
import { SMLButton } from '../../components/primitives/SMLButton';
import { SMLUpgradePromptHead } from '../../components/SMLUpgradeModal/SMLUpgradePromptHead';
import { upgradeFeatureIcons } from '../../components/SMLUpgradeModal/upgradeFeatureIcons';
import { featureMessages } from '../../components/SMLUpgradeModal/upgradeMessages';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { watermarkOfferMessages as messages } from './watermarkOfferMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WatermarkOfferMemberProps {
  files: number;
  /** False: the lede promises only new files with no watermark (spec §14.1). */
  cleanCopiesAvailable: boolean;
  onClose: () => void;
}

/**
 * The offer for a member (design: trial-upsell b4, right; the owner note of free-plans o): no
 * price, no plan and no checkout button. "Copy a message for the owner" copies one sentence with
 * the address of the app.
 */
const WatermarkOfferMemberComponent: React.FC<WatermarkOfferMemberProps> = ({
  files,
  cleanCopiesAvailable,
  onClose,
}) => {
  const intl = useIntl();
  const owner = useWorkspaceOwner();
  const { showSuccess } = useShowToast();
  const copy = useCallback(() => {
    // An insecure page (plain http) has no clipboard.
    if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
      return;
    }
    const text = intl.formatMessage(messages.ownerMessage, {
      url: `${window.location.origin}/app/home?upsell=watermark`,
    });
    navigator.clipboard
      .writeText(text)
      .then(() => showSuccess(intl.formatMessage(messages.copied)))
      .catch(console.error);
  }, [intl, showSuccess]);
  let lede = intl.formatMessage(messages.memberLedeNew);
  if (cleanCopiesAvailable) {
    lede =
      files > 0
        ? intl.formatMessage(messages.memberLede, { count: files })
        : intl.formatMessage(messages.memberLedeNoFiles);
  }

  return (
    <div className={styles.modal} data-testid="watermark-offer-member">
      <SMLUpgradePromptHead
        icon={<FontAwesomeIcon icon={upgradeFeatureIcons[SMLUpgradeFeature.watermark]} />}
        title={intl.formatMessage(featureMessages.watermarkTitle)}
        body={lede}
        onClose={onClose}
      />
      <TeamOwnerNote owner={owner} />
      <div className={styles.acts}>
        <SMLButton onClick={copy}>{intl.formatMessage(messages.copyForOwner)}</SMLButton>
      </div>
    </div>
  );
};

export const WatermarkOfferMember = TypedMemo(WatermarkOfferMemberComponent);
